import React, {useState} from 'react';
import {URLS} from "@/lib/urls";
import AdminLayout from "@/components/layout/admin/adminLayout";
import TableSearch from "@/components/table/tableSearch";
import {Can} from "@/components/can/Can";
import PermList from "@/lib/helper/PermList";
import {Button, Descriptions, Divider, Drawer, Empty, Popconfirm, Select, Space, Spin, Tag, Timeline} from "antd";
import {
    useApiDeleteAdminProductDelivery,
    useApiSearchAdminProducts,
    useApiSearchAdminProductsDelivery,
    useApiUpdateAdminProductDelivery
} from "@/api/product";
import {formatDateFull} from "@/lib/utils/date";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CommentOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    PrinterOutlined,
    SaveOutlined
} from "@ant-design/icons";
import {Link} from "react-router-dom";
import {sprintf} from "@/lib/utils/sprintf";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import RenderIf from "@/components/render-if";

// نقشه وضعیت‌ها برای نمایش
const statusMap = {
    pending: {label: 'در انتظار', color: 'gold', icon: <ClockCircleOutlined/>},
    print: {label: 'چاپ رسید', color: 'blue', icon: <PrinterOutlined/>},
    delivered: {label: 'تحویل شده', color: 'green', icon: <CheckCircleOutlined/>},
    refund: {label: 'مرجوع شده', color: 'red', icon: <ExclamationCircleOutlined/>},
    remove_lesson: {label: 'حذف درس', color: 'red', icon: <ExclamationCircleOutlined/>},
};


const statusOptions = Object.entries(statusMap).map(([value, item]) => ({
    value,
    label: item.label,
}));

// نقشه اکشن‌های تاریخچه
const actionMap = {
    notif: {label: 'پیامک', color: 'pink', icon: <CommentOutlined/>},
    print: {label: 'چاپ رسید', color: 'blue', icon: <PrinterOutlined/>},
    print_to_teacher: {label: 'چاپ رسید مربی', color: 'blue', icon: <PrinterOutlined/>},
    reprint: {label: 'چاپ مجدد رسید', color: 'blue', icon: <PrinterOutlined/>},
    deliver: {label: 'تحویل به متقاضی', color: 'green', icon: <CheckCircleOutlined/>},
    deliver_to_teacher: {label: 'تحویل به مربی', color: 'green', icon: <CheckCircleOutlined/>},
};

// محاسبه اختلاف زمانی بین دو تاریخ به دقیقه
const getTimeDifference = (date1, date2) => {
    if (!date1 || !date2) return null;
    const diffMs = new Date(date2) - new Date(date1);
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins;
};

// فرمت کردن اختلاف زمانی
const formatTimeDiff = (minutes) => {
    if (minutes === null || minutes === undefined) return '';
    if (minutes === 0) return 'هم‌زمان';
    if (minutes < 60) return `${minutes} دقیقه بعد`;
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    if (remainingMins === 0) {
        return `${hours} ساعت بعد`;
    }
    return `${hours} ساعت و ${remainingMins} دقیقه بعد`;
};

function AdminProductDeliveryList({reuseMode = false, product_delivery_step_id = null}) {
    const [loading, setLoading] = useState(false);
    const [needRefetch, setNeedRefetch] = useState(false);
    const [status, setStatus] = useState();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isEditingProduct, setIsEditingProduct] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState();
    const [isEditingStatus, setIsEditingStatus] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState();
    const [gender, setGender] = useState('')
    const [filterProduct, setFilterProduct] = useState()

    const {mutate: removeItem} = useApiDeleteAdminProductDelivery();
    const {mutate: updateDelivery, isPending: isUpdatingDelivery} = useApiUpdateAdminProductDelivery();
    const {data: products} = useApiSearchAdminProducts({per_page: 1000, status: 'published'});

    // حذف آیتم
    function handleRemove(id) {
        removeItem({id: id}, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r);
                setNeedRefetch(true);
                closeDrawer(); // بستن دراور بعد از حذف
            },
            onError: (e) => {
                ApiHandleErrorMessage(e);
            }
        });
    }

    // نمایش دراور با اطلاعات ردیف انتخاب شده
    const showDetails = (record) => {
        setSelectedItem(record);
        setSelectedProductId(record?.product?.id || record?.product_id);
        setSelectedStatus(record?.status);
        setIsEditingProduct(false);
        setIsEditingStatus(false);
        setDrawerVisible(true);
    };

    // بستن دراور
    const closeDrawer = () => {
        setDrawerVisible(false);
        setSelectedItem(null);
        setIsEditingProduct(false);
        setIsEditingStatus(false);
        setSelectedProductId(undefined);
        setSelectedStatus(undefined);
    };

    function applyUpdatedDelivery(response, extra = {}) {
        const updatedDelivery = response?.data?.data;
        const updatedProduct = (products?.data?.data || []).find((item) => item.id === extra.product_id);
        setSelectedItem((prev) => ({
            ...(updatedDelivery || prev),
            ...extra,
            product: updatedDelivery?.product || updatedProduct || prev?.product,
        }));
        setNeedRefetch(true);
    }

    function handleUpdateProduct() {
        if (!selectedItem?.id || !selectedProductId) {
            return;
        }

        updateDelivery({
            id: selectedItem.id,
            product_id: selectedProductId,
        }, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r);
                applyUpdatedDelivery(r, {product_id: selectedProductId});
                setIsEditingProduct(false);
            },
            onError: (e) => {
                ApiHandleErrorMessage(e);
            }
        });
    }

    function handleUpdateStatus() {
        if (!selectedItem?.id || !selectedStatus) {
            return;
        }

        updateDelivery({
            id: selectedItem.id,
            status: selectedStatus,
        }, {
            onSuccess: (r) => {
                ApiHandleSuccessMessage(r);
                applyUpdatedDelivery(r, {status: selectedStatus});
                setIsEditingStatus(false);
            },
            onError: (e) => {
                ApiHandleErrorMessage(e);
            }
        });
    }

    const columns = [
        {
            title: 'کد تحویل',
            dataIndex: 'code',
            key: 'code',
            width: 120,
            responsive: ['md'],
        },
        {
            title: 'نام محصول',
            dataIndex: ['product', 'title'],
            key: 'product_title',
            width: 150,
            responsive: ['md'],
            render: (text, record) => record.product?.title || '-',
        },
        {
            title: 'دریافت کننده',
            key: 'receiver',
            width: 150,
            responsive: ['md'],
            render: (text, record) => {
                const user = record.useracademy;
                return user ? `${user.firstname} ${user.lastname}` : '-';
            },
        },
        {
            title: 'کد ملی',
            dataIndex: ['useracademy', 'nationalcode'],
            key: 'nationalcode',
            width: 130,
            responsive: ['md'],
            render: (text, record) => record.useracademy?.nationalcode || '-',
        },
        {
            title: 'شماره همراه',
            dataIndex: ['useracademy', 'mobile'],
            key: 'mobile',
            width: 130,
            responsive: ['md'],
            render: (text, record) => record.useracademy?.mobile || '-',
        },
        {
            title: 'وضعیت',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            responsive: ['md'],
            render: (statusKey) => {
                const statusInfo = statusMap[statusKey] || {label: statusKey, color: 'default'};
                return (
                    <Tag color={statusInfo.color} icon={statusInfo.icon}>
                        {statusInfo.label}
                    </Tag>
                );
            },
        },
        {
            title: 'گروه تحویل',
            dataIndex: 'product_delivery_step_id',
            key: 'product_delivery_step_id',
            width: 100,
            responsive: ['md'],
            render: (text, record) => (
                record?.product_delivery_step?.id ? (
                    <Link to={sprintf(URLS.admin.products.delivery.edit, {id: record.product_delivery_step.id})}>
                        {record.product_delivery_step.id}
                    </Link>
                ) : '-'
            ),
        },
        {
            title: 'تاریخ ثبت',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            responsive: ['md'],
            render: (date) => date ? formatDateFull(date) : '-',
        },
        {
            title: '',
            dataIndex: 'id',
            key: 'action',
            width: 100,
            align: 'center',
            responsive: ['md'],
            render: (text, record) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined/>}
                    onClick={() => showDetails(record)}
                >
                    جزئیات
                </Button>
            ),
        },
        // Mobile view
        {
            title: '',
            key: 'mobile',
            responsive: ['xs', 'sm'],
            render: (_, record) => {
                const user = record?.useracademy;
                const product = record?.product;
                const deliveryStep = record?.product_delivery_step;
                const statusInfo = statusMap[record?.status] || {label: record?.status || '-', color: 'default'};

                return (
                    <div className="flex md:hidden flex-col gap-3 py-1">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs  ">کد تحویل</span>
                            <span className="font-mono font-medium">{record?.code || '-'}</span>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs  ">نام محصول</span>
                            <span className="font-medium text-end">{product?.title || '-'}</span>
                        </div>

                        <div className="flex flex-col gap-1">
                            <span className="text-xs  ">دریافت کننده</span>
                            <div className="font-medium">
                                {user ? `${user.firstname} ${user.lastname}` : '-'}
                            </div>
                            {user?.mobile && (
                                <div className="text-xs  ">{user.mobile}</div>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs  ">کد ملی</span>
                            <span className="font-mono">{user?.nationalcode || '-'}</span>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs  ">وضعیت</span>
                            <Tag color={statusInfo.color} icon={statusInfo.icon}>
                                {statusInfo.label}
                            </Tag>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs  ">گروه تحویل</span>
                            {deliveryStep?.id ? (
                                <Link to={sprintf(URLS.admin.products.delivery.edit, {id: deliveryStep.id})}>
                                    {deliveryStep.id}
                                </Link>
                            ) : '-'}
                        </div>

                        <div className="pt-1">
                            <Button
                                type="primary"
                                size="small"
                                icon={<EyeOutlined/>}
                                onClick={() => showDetails(record)}
                                block
                            >
                                نمایش جزئیات
                            </Button>
                        </div>
                    </div>
                );
            },
        },
    ];

    const pageContent = <>
        <Spin spinning={loading}>
            <TableSearch
                needRefetch={needRefetch}
                setNeedRefetch={setNeedRefetch}
                searchInput={true}
                addButton={
                    <div className="flex justify-center gap-1 w-full">
                        <Select
                            onChange={(e) => setStatus(e)}
                            placeholder="وضعیت"
                            allowClear={true}
                            onClear={() => setStatus('')}
                            className="min-w-md w-full"
                        >
                            {statusOptions.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                        <Select
                            onChange={(e) => setGender(e)}
                            placeholder="جنسیت"
                            allowClear={true}
                            onClear={() => setGender('')}
                            className="min-w-md w-full"
                        >

                            <Select.Option key={'male'} value={'male'}>
                                پسر
                            </Select.Option>

                            <Select.Option key={'female'} value={'female'}>
                                دختر
                            </Select.Option>

                        </Select>

                        <Select
                            showSearch
                            className="w-full"
                            placeholder="فیلتر محصول"
                            value={filterProduct}
                            optionFilterProp="label"
                            options={
                                (products?.data?.data || []).map((item) => ({
                                    label: item.title,
                                    value: item.id,
                                }))
                            }
                            onChange={setFilterProduct}
                        />
                    </div>
                }
                apiFn={useApiSearchAdminProductsDelivery}
                fnArgs={{
                    product_id: filterProduct,
                    status: status,
                    gender: gender,
                    product_delivery_step_id: product_delivery_step_id
                }}
                columns={columns}
                scroll={{x: 1200}}
                responsive={true}
                rowKey="id"
            />
        </Spin>

        {/* Drawer نمایش جزئیات */}
        <Drawer
            title="جزئیات اقلام قابل تحویل"
            placement="left"
            open={drawerVisible}
            onClose={closeDrawer}
            width={700}
            destroyOnClose
        >
            {selectedItem && (
                <div className="space-y-6">
                    {/* اطلاعات اصلی */}
                    <Descriptions
                        title="اطلاعات اصلی"
                        bordered
                        column={{xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1}}
                        size="small"
                    >
                        <Descriptions.Item label="کد تحویل">
                            <span className="font-mono font-bold">{selectedItem.code}</span>
                        </Descriptions.Item>
                        <Descriptions.Item label="وضعیت">
                            {isEditingStatus ? (
                                <Space direction="vertical" className="w-full" size="small">
                                    <Select
                                        className="w-full"
                                        placeholder="انتخاب وضعیت"
                                        value={selectedStatus}
                                        options={statusOptions}
                                        onChange={setSelectedStatus}
                                    />
                                    <Space>
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<SaveOutlined/>}
                                            loading={isUpdatingDelivery}
                                            disabled={!selectedStatus}
                                            onClick={handleUpdateStatus}
                                        >
                                            ذخیره وضعیت
                                        </Button>
                                        <Button
                                            size="small"
                                            disabled={isUpdatingDelivery}
                                            onClick={() => {
                                                setSelectedStatus(selectedItem.status);
                                                setIsEditingStatus(false);
                                            }}
                                        >
                                            انصراف
                                        </Button>
                                    </Space>
                                </Space>
                            ) : (
                                <div className="flex items-center justify-between gap-2 w-full">
                                    <Tag color={statusMap[selectedItem.status]?.color}
                                         icon={statusMap[selectedItem.status]?.icon}>
                                        {statusMap[selectedItem.status]?.label || selectedItem.status}
                                    </Tag>
                                    <Can permission={PermList.PRODUCT_DELIVERY_EDIT}>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined/>}
                                            onClick={() => {
                                                setSelectedStatus(selectedItem.status);
                                                setIsEditingStatus(true);
                                            }}
                                        >
                                            ویرایش وضعیت
                                        </Button>
                                    </Can>
                                </div>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="نام محصول" span={2}>
                            {isEditingProduct ? (
                                <Space direction="vertical" className="w-full" size="small">
                                    <Select
                                        showSearch
                                        className="w-full"
                                        placeholder="انتخاب محصول"
                                        value={selectedProductId}
                                        optionFilterProp="label"
                                        options={
                                            (products?.data?.data || []).map((item) => ({
                                                label: item.title,
                                                value: item.id,
                                            }))
                                        }
                                        onChange={setSelectedProductId}
                                    />
                                    <Space>
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<SaveOutlined/>}
                                            loading={isUpdatingDelivery}
                                            disabled={!selectedProductId}
                                            onClick={handleUpdateProduct}
                                        >
                                            ذخیره محصول
                                        </Button>
                                        <Button
                                            size="small"
                                            disabled={isUpdatingDelivery}
                                            onClick={() => {
                                                setSelectedProductId(selectedItem.product?.id || selectedItem.product_id);
                                                setIsEditingProduct(false);
                                            }}
                                        >
                                            انصراف
                                        </Button>
                                    </Space>
                                </Space>
                            ) : (
                                <div className="flex items-center justify-between gap-2 w-full">
                                    <span>{selectedItem.product?.title || '-'}</span>
                                    <Can permission={PermList.PRODUCT_DELIVERY_EDIT}>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined/>}
                                            onClick={() => {
                                                setSelectedProductId(selectedItem.product?.id || selectedItem.product_id);
                                                setIsEditingProduct(true);
                                            }}
                                        >
                                            ویرایش محصول
                                        </Button>
                                    </Can>
                                </div>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="گروه تحویل" span={2}>
                            {selectedItem.product_delivery_step?.id ? (
                                <Link
                                    to={sprintf(URLS.admin.products.delivery.edit, {id: selectedItem.product_delivery_step.id})}>
                                    گروه {selectedItem.product_delivery_step.id}
                                </Link>
                            ) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="تاریخ ثبت" span={2}>
                            {selectedItem.created_at ? formatDateFull(selectedItem.created_at) : '-'}
                        </Descriptions.Item>
                    </Descriptions>

                    {/* اطلاعات دریافت‌کننده */}
                    <Descriptions
                        title="اطلاعات دریافت‌کننده"
                        bordered
                        column={{xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1}}
                        size="small"
                    >
                        <Descriptions.Item label="نام و نام خانوادگی" span={2}>
                            {selectedItem.useracademy ?
                                `${selectedItem.useracademy.firstname} ${selectedItem.useracademy.lastname}` :
                                '-'
                            }
                        </Descriptions.Item>
                        <Descriptions.Item label="کد ملی">
                            {selectedItem.useracademy?.nationalcode || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="شماره همراه">
                            {selectedItem.useracademy?.mobile || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="کد دانش‌آموزی" span={2}>
                            {selectedItem.useracademy?.code || '-'}
                        </Descriptions.Item>
                    </Descriptions>

                    {/* تاریخچه (تایم‌لاین) - یک طرفه */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4">تاریخچه عملیات</h3>
                        {selectedItem.histories && selectedItem.histories.length > 0 ? (
                            <Timeline mode="left" className="mt-2">
                                {selectedItem.histories
                                    .sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
                                    .map((history, index, array) => {
                                        const actionInfo = actionMap[history.action] || {
                                            label: history.action,
                                            color: 'default'
                                        };
                                        const prevDate = index > 0 ? array[index - 1].datetime : null;
                                        const timeDiff = prevDate ? getTimeDifference(prevDate, history.datetime) : null;
                                        const timeDiffText = timeDiff !== null ? formatTimeDiff(timeDiff) : '';

                                        return (
                                            <Timeline.Item
                                                key={history.id || index}
                                                color={actionInfo.color}
                                                dot={actionInfo.icon || <ClockCircleOutlined/>}
                                            >
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <Tag color={actionInfo.color}>
                                                            {actionInfo.label}
                                                        </Tag>
                                                        <span className="text-sm font-medium">
                                                                {history.expert ?
                                                                    `${history.expert.firstname} ${history.expert.lastname}` :
                                                                    'نامشخص'
                                                                }
                                                            </span>
                                                        <RenderIf condition={history.teacher?.id}>
                                                            <span className="text-sm font-medium">
                                                                <Tag>
                                                                    <div className="flex gap-1 flex-wrap">

                                                                    <span>مربی تحویل گیرنده:</span>

                                                                    <span className={'font-bold'}>{history?.teacher?.firstname}</span>
                                                                    <span className={'font-bold'}>{history?.teacher?.lastname}</span>
                                                                    <span>{history?.teacher?.mobile}</span>

                                                                    </div>
                                                                </Tag>
                                                            </span>
                                                        </RenderIf>

                                                    </div>

                                                    <div className="text-xs  ">
                                                        {history.datetime ? formatDateFull(history.datetime) : '-'}
                                                    </div>

                                                    {timeDiffText && (
                                                        <div className="text-xs  ">
                                                            <ClockCircleOutlined className="ml-1"/>
                                                            {timeDiffText}
                                                        </div>
                                                    )}

                                                </div>
                                            </Timeline.Item>
                                        );
                                    })}
                            </Timeline>
                        ) : (
                            <Empty description="هیچ تاریخچه‌ای برای این آیتم وجود ندارد"/>
                        )}
                    </div>

                    {/* توضیحات گروه تحویل */}
                    {selectedItem.product_delivery_step?.description && (
                        <div>
                            <h3 className="text-lg font-semibold mb-2">توضیحات گروه تحویل</h3>
                            <div className="  p-3 rounded-lg">
                                {selectedItem.product_delivery_step.description}
                            </div>
                        </div>
                    )}

                    <Divider/>

                    {/* دکمه حذف در پایین دراور */}
                    <Can permission={PermList.PRODUCT_DELIVERY_DELETE}>
                        <div className="flex justify-center pt-2">
                            <Popconfirm
                                title="حذف اقلام قابل تحویل"
                                description="آیا از حذف این مورد مطمئن هستید؟"
                                onConfirm={() => handleRemove(selectedItem.id)}
                                okText="بله، حذف شود"
                                cancelText="انصراف"
                                placement="top"
                            >
                                <Button
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined/>}
                                    className=""
                                >
                                    حذف این آیتم
                                </Button>
                            </Popconfirm>
                        </div>
                    </Can>
                </div>
            )}
        </Drawer>
    </>

    if (reuseMode) {
        return pageContent
    }
    return (
        <AdminLayout backTo={URLS.admin.products.dashboard} pageTitle={'لیست اقلام قابل تحویل'}>
            {pageContent}
        </AdminLayout>
    );
}

export default AdminProductDeliveryList;